import React, { useEffect, useRef, useState } from "react";
import { api, API, getToken } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "./ui/dialog";
import { Wand2, UploadCloud, Plus, Trash2, Loader2, Sparkles, ChevronDown, ChevronRight, ListTree, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const emptyVisit = () => ({ _k: Math.random().toString(36).slice(2), visit_name: "", visit_code: "", grant_amount: 0, frequency: "per patient", activities: [] });
const emptyActivity = () => ({ _k: Math.random().toString(36).slice(2), name: "", amount: 0, description: "" });
const emptyPT = () => ({ _k: Math.random().toString(36).slice(2), name: "", amount: 0, description: "" });
const emptySF = () => ({ _k: Math.random().toString(36).slice(2), name: "", amount: 0 });
const emptyInv = () => ({ _k: Math.random().toString(36).slice(2), name: "", amount: 0, category: "other", description: "", when_billable: "", frequency: "" });

const INV_CATEGORIES = [
  "startup","closeout","regulatory","marketing","advertising","training",
  "monitoring","pharmacy","records","milestone","amendment","screen_fail",
  "unscheduled","investigator_meeting","database_lock","other",
];

const CAT_COLORS = {
  startup: "bg-blue-50 text-blue-800 border-blue-200",
  closeout: "bg-purple-50 text-purple-800 border-purple-200",
  regulatory: "bg-amber-50 text-amber-800 border-amber-200",
  marketing: "bg-pink-50 text-pink-800 border-pink-200",
  advertising: "bg-pink-50 text-pink-800 border-pink-200",
  training: "bg-cyan-50 text-cyan-800 border-cyan-200",
  monitoring: "bg-indigo-50 text-indigo-800 border-indigo-200",
  pharmacy: "bg-teal-50 text-teal-800 border-teal-200",
  records: "bg-slate-100 text-slate-700 border-slate-200",
  milestone: "bg-emerald-50 text-emerald-800 border-emerald-200",
  amendment: "bg-amber-50 text-amber-800 border-amber-200",
  screen_fail: "bg-red-50 text-red-800 border-red-200",
  unscheduled: "bg-orange-50 text-orange-800 border-orange-200",
  investigator_meeting: "bg-blue-50 text-blue-800 border-blue-200",
  database_lock: "bg-violet-50 text-violet-800 border-violet-200",
  other: "bg-slate-100 text-slate-700 border-slate-200",
};

const DEFAULTS = {
  study_name: "", protocol_number: "", sponsor: "", cro: "",
  therapeutic_area: "", phase: "Phase II", site_id: "", pi_name: "",
  contract_execution_date: "", study_start_date: "", study_close_date: "",
  total_budget: 0, payment_model: "Per-Visit",
  pi_split_percent: 0, clinic_fee_percent: 0,
  payment_terms: "Net-30", payment_trigger: "invoice submit",
  visit_grants: [], pass_throughs: [], startup_fees: [], invoicables: [],
  source_text: null,
};

export default function CTADocumentImport({ sites, onCreated }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("upload"); // upload | review
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(DEFAULTS);
  const [sourceFile, setSourceFile] = useState("");
  const [coverage, setCoverage] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);
  const [rescanning, setRescanning] = useState(false);
  const inputRef = useRef();

  // Live coverage recompute on edits (debounced)
  useEffect(() => {
    if (step !== "review") return;
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/ctas/coverage-preview", { cta: form });
        setCoverage(r.data);
      } catch { /* ignore */ }
    }, 400);
    return () => clearTimeout(t);
  }, [form, step]);

  const reset = () => {
    setStep("upload"); setForm(DEFAULTS); setSourceFile(""); setExtracting(false); setSaving(false); setCoverage(null); setOriginalFile(null); setRescanning(false);
  };

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setExtracting(true);
    setSourceFile(f.name);
    setOriginalFile(f);
    const fd = new FormData();
    fd.append("file", f);
    const progressToast = toast.loading(`AI extracting ${f.name} — this can take 30–120s for large PDFs…`);
    try {
      // Kick off async job — returns immediately with a job_id (with 1 retry on network blip)
      let r;
      let kickoffAttempts = 0;
      while (kickoffAttempts < 2) {
        try {
          r = await fetch(`${API}/ctas/extract-document-async`, {
            method: "POST",
            headers: { Authorization: `Bearer ${getToken()}` },
            body: fd,
          });
          break;
        } catch (netErr) {
          kickoffAttempts += 1;
          if (kickoffAttempts >= 2) {
            throw new Error(`Upload failed: ${netErr.message}. Check your connection and retry.`);
          }
          await new Promise((res) => setTimeout(res, 2000));
        }
      }
      if (!r.ok) {
        let detail = `HTTP ${r.status} ${r.statusText}`;
        try {
          const errJson = await r.json();
          detail = errJson.detail || JSON.stringify(errJson).slice(0, 300);
        } catch {
          try {
            const txt = await r.text();
            if (txt) detail = txt.slice(0, 300);
          } catch { /* keep status */ }
        }
        throw new Error(detail);
      }
      const startResp = await r.json();
      const jobId = startResp.job_id;
      if (!jobId) throw new Error("No job_id returned");

      // Poll for completion (max ~5 minutes) with resilient retry
      const POLL_INTERVAL_MS = 2500;
      const MAX_POLLS = 120; // 5 min ceiling
      const MAX_CONSEC_NETWORK_ERRORS = 8; // ~20s of network blips before giving up
      let data = null;
      let consecNetworkErrors = 0;
      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise((res) => setTimeout(res, POLL_INTERVAL_MS));
        let jr;
        try {
          jr = await fetch(`${API}/ctas/extract-jobs/${jobId}`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          });
        } catch (netErr) {
          // Network blip / fetch failed — count and continue
          consecNetworkErrors += 1;
          if (consecNetworkErrors >= MAX_CONSEC_NETWORK_ERRORS) {
            throw new Error(`Network unstable: ${MAX_CONSEC_NETWORK_ERRORS} consecutive polling failures (${netErr.message}). The extraction may still finish — refresh the page in a minute.`);
          }
          continue;
        }
        if (jr.ok) {
          consecNetworkErrors = 0;
          let job;
          try {
            job = await jr.json();
          } catch {
            // Bad JSON (e.g. CF error page) — treat as transient
            consecNetworkErrors += 1;
            continue;
          }
          if (job.status === "done") {
            data = { data: job.data, coverage: job.coverage };
            break;
          }
          if (job.status === "error") {
            throw new Error(job.error || "Extraction failed");
          }
          // still running — keep polling
        } else if (jr.status === 404) {
          throw new Error("Extraction job expired before completion. Try uploading again.");
        } else {
          // 5xx / gateway / etc — treat as transient blip
          consecNetworkErrors += 1;
          if (consecNetworkErrors >= MAX_CONSEC_NETWORK_ERRORS) {
            throw new Error(`Gateway unstable: ${jr.status} ${jr.statusText} (${MAX_CONSEC_NETWORK_ERRORS} consecutive failures).`);
          }
        }
      }
      if (!data) throw new Error("Extraction timed out (>5 minutes). Try a smaller PDF or split the file.");
      const d = data.data || {};
      setForm({
        ...DEFAULTS,
        ...Object.fromEntries(Object.entries(d).filter(([k, v]) => !k.startsWith("_") && v !== null && v !== undefined)),
        source_text: d._source_text || null,  // persist raw text for Contract Review
        visit_grants: (d.visit_grants || []).map((v) => ({
          _k: Math.random().toString(36).slice(2),
          ...v,
          activities: (v.activities || []).map((a) => ({ _k: Math.random().toString(36).slice(2), ...a })),
        })),
        pass_throughs: (d.pass_throughs || []).map((v) => ({ _k: Math.random().toString(36).slice(2), ...v })),
        startup_fees: (d.startup_fees || []).map((v) => ({ _k: Math.random().toString(36).slice(2), ...v })),
        invoicables: (d.invoicables || []).map((v) => ({ _k: Math.random().toString(36).slice(2), category: "other", ...v })),
      });
      setStep("review");
      if (data.coverage) setCoverage(data.coverage);
      const counts = d._counts || {};
      toast.success(`Extracted: ${counts.visits || 0} visits · ${counts.activities || 0} activities · ${counts.invoicables || 0} invoicables · ${counts.pass_throughs || 0} pass-throughs`);
    } catch (err) {
      toast.error(err.message, { duration: 12000 });
      console.error("[AI Import] extract-document failed:", err);
    } finally {
      toast.dismiss(progressToast);
      setExtracting(false);
      e.target.value = "";
    }
  };

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const rescan = async () => {
    if (!originalFile) {
      toast.error("Original file no longer in memory — re-upload to rescan");
      return;
    }
    setRescanning(true);
    try {
      const fd = new FormData();
      fd.append("file", originalFile);
      const missing = (coverage?.missing_categories || []).map((m) => m.category).join(",");
      fd.append("missing_categories", missing);
      // Send only what the backend needs for de-dup
      fd.append("existing", JSON.stringify({
        invoicables: form.invoicables.map((i) => ({ name: i.name, amount: i.amount, category: i.category })),
        visit_grants: form.visit_grants.map((v) => ({
          visit_code: v.visit_code, visit_name: v.visit_name, grant_amount: v.grant_amount,
          activities: (v.activities || []).map((a) => ({ name: a.name })),
        })),
      }));
      const r = await fetch(`${API}/ctas/rescan-gaps`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Rescan failed");
      const ex = data.extras || {};
      // Merge: append extras into form arrays with new keys
      const newInv = (ex.extra_invoicables || []).map((i) => ({ _k: Math.random().toString(36).slice(2), category: "other", ...i }));
      const newPT = (ex.extra_pass_throughs || []).map((i) => ({ _k: Math.random().toString(36).slice(2), ...i }));

      // Merge activities into matching visits
      const visits = [...form.visit_grants];
      let actAdded = 0;
      for (const a of (ex.extra_activities || [])) {
        const idx = visits.findIndex((v) => (v.visit_code || "").toLowerCase() === (a.visit_code || "").toLowerCase());
        if (idx >= 0) {
          const next = [...(visits[idx].activities || []), { _k: Math.random().toString(36).slice(2), name: a.name, amount: a.amount, description: a.description }];
          visits[idx] = { ...visits[idx], activities: next };
          actAdded += 1;
        }
      }

      setForm((p) => ({
        ...p,
        invoicables: [...p.invoicables, ...newInv],
        pass_throughs: [...p.pass_throughs, ...newPT],
        visit_grants: visits,
      }));

      const total = newInv.length + newPT.length + actAdded;
      if (total > 0) {
        toast.success(`Found ${total} more item${total !== 1 ? "s" : ""}: ${newInv.length} invoicables, ${actAdded} activities, ${newPT.length} pass-throughs`);
      } else {
        toast.info(ex.notes || "No additional items found in the document.");
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRescanning(false);
    }
  };

  const updateRow = (arr, i, k, v) => {
    const next = [...form[arr]];
    next[i] = { ...next[i], [k]: v };
    setF(arr, next);
  };
  const removeRow = (arr, i) => setF(arr, form[arr].filter((_, j) => j !== i));

  const save = async () => {
    if (!form.site_id) return toast.error("Select a site before saving");
    if (!form.study_name || !form.sponsor) return toast.error("Study name and sponsor required");
    setSaving(true);
    try {
      // eslint-disable-next-line no-unused-vars
      const stripK = ({ _k, ...rest }) => rest;
      const visits = form.visit_grants.filter((r) => r.visit_code).map((v) => ({
        ...stripK(v),
        grant_amount: Number(v.grant_amount || 0),
        activities: (v.activities || []).filter((a) => a.name).map((a) => ({
          ...stripK(a),
          amount: Number(a.amount || 0),
        })),
      }));
      const invs = form.invoicables.filter((r) => r.name).map((i) => ({
        ...stripK(i),
        amount: Number(i.amount || 0),
      }));
      const pts = form.pass_throughs.filter((r) => r.name).map((p) => ({
        ...stripK(p), amount: Number(p.amount || 0),
      }));
      const sfs = form.startup_fees.filter((r) => r.name).map((s) => ({
        ...stripK(s), amount: Number(s.amount || 0),
      }));
      await api.post("/ctas", {
        ...form,
        total_budget: Number(form.total_budget || 0),
        pi_split_percent: Number(form.pi_split_percent || 0),
        clinic_fee_percent: Number(form.clinic_fee_percent || 0),
        visit_grants: visits,
        pass_throughs: pts,
        startup_fees: sfs,
        invoicables: invs,
      });
      toast.success("CTA created from document");
      setOpen(false);
      reset();
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="ai-import-btn">
          <Sparkles className="w-4 h-4 mr-1" style={{ color: "#F05D23" }} /> AI Import
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-[#0A4C95]" /> AI Document Import
            {step === "review" && <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 ml-2">Extracted · {sourceFile}</Badge>}
          </DialogTitle>
        </DialogHeader>

        {step === "upload" && (
          <div className="py-6">
            <div
              className="border-2 border-dashed border-slate-300 rounded-lg p-10 text-center hover:border-[#0A4C95] transition-colors cursor-pointer"
              onClick={() => inputRef.current?.click()}
              data-testid="ai-upload-drop"
            >
              {extracting ? (
                <>
                  <Loader2 className="w-10 h-10 mx-auto text-[#0A4C95] animate-spin" />
                  <div className="mt-3 text-sm font-semibold">Reading document...</div>
                  <div className="text-xs text-slate-500 mt-1">Claude Sonnet 4.5 is extracting fields · usually 5–20 seconds</div>
                </>
              ) : (
                <>
                  <UploadCloud className="w-10 h-10 mx-auto text-slate-400" />
                  <div className="mt-3 text-sm font-semibold">Drop a CTA document</div>
                  <div className="text-xs text-slate-500 mt-1">PDF · DOCX · XLSX · TXT · up to ~80 pages</div>
                  <Button className="mt-4 bg-[#0A4C95] hover:bg-[#083d79]" data-testid="ai-pick-file-btn">Choose File</Button>
                </>
              )}
              <input
                ref={inputRef}
                type="file"
                hidden
                accept=".pdf,.docx,.xlsx,.xlsm,.txt"
                onChange={handleUpload}
                data-testid="ai-file-input"
              />
            </div>
            <div className="mt-5 text-xs text-slate-500 bg-blue-50 border border-blue-200 rounded p-3">
              <strong>How it works:</strong> We extract the document text, send it to Claude Sonnet 4.5 with a structured schema, and pre-fill the CTA form below. You'll review and edit before saving — nothing is stored until you click <em>Save CTA</em>.
            </div>
          </div>
        )}

        {step === "review" && (
          <div className="py-2 space-y-4" data-testid="ai-review-form">
            {coverage && <CoveragePanel coverage={coverage} onRescan={rescan} rescanning={rescanning} canRescan={!!originalFile} />}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Study Name</Label><Input value={form.study_name} onChange={(e) => setF("study_name", e.target.value)} data-testid="ai-study-name" /></div>
              <div><Label>Protocol #</Label><Input value={form.protocol_number} onChange={(e) => setF("protocol_number", e.target.value)} /></div>
              <div><Label>Sponsor</Label><Input value={form.sponsor} onChange={(e) => setF("sponsor", e.target.value)} /></div>
              <div><Label>CRO</Label><Input value={form.cro} onChange={(e) => setF("cro", e.target.value)} /></div>
              <div><Label>Therapeutic Area</Label><Input value={form.therapeutic_area} onChange={(e) => setF("therapeutic_area", e.target.value)} /></div>
              <div>
                <Label>Phase</Label>
                <Select value={form.phase} onValueChange={(v) => setF("phase", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["Phase I","Phase II","Phase III","Phase IV"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Site *</Label>
                <Select value={form.site_id} onValueChange={(v) => setF("site_id", v)}>
                  <SelectTrigger data-testid="ai-site-select"><SelectValue placeholder="Select site (required)" /></SelectTrigger>
                  <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>PI Name</Label><Input value={form.pi_name} onChange={(e) => setF("pi_name", e.target.value)} /></div>
              <div><Label>Total Budget ($)</Label><Input type="number" value={form.total_budget} onChange={(e) => setF("total_budget", e.target.value)} /></div>
              <div>
                <Label>Payment Model</Label>
                <Select value={form.payment_model} onValueChange={(v) => setF("payment_model", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["Per-Visit","Flat Rate","Per-Procedure","Milestone","Hybrid"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>PI Split %</Label><Input type="number" value={form.pi_split_percent} onChange={(e) => setF("pi_split_percent", e.target.value)} /></div>
              <div><Label>Clinic Fee %</Label><Input type="number" value={form.clinic_fee_percent} onChange={(e) => setF("clinic_fee_percent", e.target.value)} /></div>
              <div>
                <Label>Payment Terms</Label>
                <Select value={form.payment_terms} onValueChange={(v) => setF("payment_terms", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["Net-30","Net-45","Net-60"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Payment Trigger</Label><Input value={form.payment_trigger} onChange={(e) => setF("payment_trigger", e.target.value)} /></div>
              <div><Label>Execution Date</Label><Input type="date" value={form.contract_execution_date} onChange={(e) => setF("contract_execution_date", e.target.value)} /></div>
              <div><Label>Start Date</Label><Input type="date" value={form.study_start_date} onChange={(e) => setF("study_start_date", e.target.value)} /></div>
            </div>

            <VisitGrantsSection
              visits={form.visit_grants}
              onChange={(visits) => setF("visit_grants", visits)}
            />

            <InvoicablesSection
              rows={form.invoicables}
              onAdd={() => setF("invoicables", [...form.invoicables, emptyInv()])}
              onChange={(invs) => setF("invoicables", invs)}
            />

            <RowSection
              title="Pass-Throughs"
              rows={form.pass_throughs}
              onAdd={() => setF("pass_throughs", [...form.pass_throughs, emptyPT()])}
              onRemove={(i) => removeRow("pass_throughs", i)}
              render={(v, i) => (
                <>
                  <Input className="col-span-4" placeholder="Name" value={v.name} onChange={(e) => updateRow("pass_throughs", i, "name", e.target.value)} />
                  <Input className="col-span-3" type="number" placeholder="$" value={v.amount} onChange={(e) => updateRow("pass_throughs", i, "amount", e.target.value)} />
                  <Input className="col-span-4" placeholder="Description" value={v.description || ""} onChange={(e) => updateRow("pass_throughs", i, "description", e.target.value)} />
                </>
              )}
            />

            <RowSection
              title="Startup Fees (legacy)"
              rows={form.startup_fees}
              onAdd={() => setF("startup_fees", [...form.startup_fees, emptySF()])}
              onRemove={(i) => removeRow("startup_fees", i)}
              render={(v, i) => (
                <>
                  <Input className="col-span-7" placeholder="Name" value={v.name} onChange={(e) => updateRow("startup_fees", i, "name", e.target.value)} />
                  <Input className="col-span-4" type="number" placeholder="$" value={v.amount} onChange={(e) => updateRow("startup_fees", i, "amount", e.target.value)} />
                </>
              )}
            />
          </div>
        )}

        <DialogFooter>
          {step === "review" && (
            <>
              <Button variant="ghost" onClick={reset}>Upload Different File</Button>
              <Button variant="outline" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-[#0A4C95] hover:bg-[#083d79]" data-testid="ai-save-btn">
                {saving ? "Saving..." : "Save CTA"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CoveragePanel({ coverage, onRescan, rescanning, canRescan }) {
  const { score, band, breakdown, gaps, summary } = coverage;
  const showRescan = score < 80 && canRescan && onRescan;
  const bandColor = {
    excellent: "bg-emerald-50 border-emerald-300 text-emerald-900",
    good: "bg-blue-50 border-blue-300 text-blue-900",
    fair: "bg-amber-50 border-amber-300 text-amber-900",
    poor: "bg-red-50 border-red-300 text-red-900",
  }[band] || "bg-slate-50 border-slate-300";
  const scoreColor = {
    excellent: "text-emerald-700",
    good: "text-blue-700",
    fair: "text-amber-700",
    poor: "text-red-700",
  }[band] || "text-slate-700";

  const sevColor = {
    high: "text-red-700 bg-red-100 border-red-200",
    medium: "text-amber-700 bg-amber-100 border-amber-200",
    low: "text-slate-600 bg-slate-100 border-slate-200",
  };

  const Bar = ({ label, val, max }) => (
    <div className="flex-1">
      <div className="flex justify-between text-[10.5px] text-slate-600 font-semibold uppercase tracking-wider">
        <span>{label}</span><span className="font-mono-fin">{val}/{max}</span>
      </div>
      <div className="bg-slate-200 rounded h-1.5 mt-1 overflow-hidden">
        <div className="h-full bg-[#0A4C95]" style={{ width: `${(val / max) * 100}%` }} />
      </div>
    </div>
  );

  return (
    <div className={`border-2 rounded-lg p-4 ${bandColor}`} data-testid="coverage-panel">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" />
            <span className="text-[10.5px] font-bold uppercase tracking-wider">Budget Coverage</span>
          </div>
          <div className="flex items-baseline gap-1 mt-1">
            <span className={`font-mono-fin text-4xl font-extrabold ${scoreColor}`} data-testid="coverage-score">{score}</span>
            <span className="text-sm text-slate-500">/100</span>
          </div>
          <Badge variant="outline" className="mt-1 capitalize">{band}</Badge>
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-start gap-2">
            <div className="flex-1 flex gap-3">
              <Bar label="Activities" val={breakdown.activity_coverage} max={breakdown.activity_coverage_max} />
              <Bar label="Invoicables" val={breakdown.invoicable_coverage} max={breakdown.invoicable_coverage_max} />
              <Bar label="Schedule" val={breakdown.schedule_completeness} max={breakdown.schedule_completeness_max} />
            </div>
            {showRescan && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 bg-white shrink-0"
                onClick={onRescan}
                disabled={rescanning}
                data-testid="rescan-gaps-btn"
                title="Re-send the document to Claude with a targeted prompt for the missing categories"
              >
                {rescanning ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" style={{ color: "#F05D23" }} />}
                {rescanning ? "Re-scanning..." : "Re-scan for gaps"}
              </Button>
            )}
          </div>
          <div className="text-[11px] text-slate-600 flex flex-wrap gap-x-4 gap-y-0.5">
            <span><strong>{summary.visits}</strong> visits</span>
            <span><strong>{summary.activities_total}</strong> activities</span>
            <span><strong>{summary.invoicables}</strong> invoicables</span>
            <span><strong>{summary.pass_throughs}</strong> pass-throughs</span>
            {summary.stated_total_budget > 0 && <span>Stated total: <strong>${summary.stated_total_budget.toLocaleString()}</strong></span>}
          </div>
        </div>
      </div>
      {gaps.length > 0 && (
        <details className="mt-3" open={gaps.some((g) => g.severity === "high")}>
          <summary className="cursor-pointer text-[12px] font-semibold flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            {gaps.length} gap{gaps.length !== 1 ? "s" : ""} found
          </summary>
          <ul className="mt-2 space-y-1">
            {gaps.map((g, i) => (
              <li key={i} className="text-[11.5px] flex items-start gap-2">
                <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${sevColor[g.severity] || sevColor.low}`}>{g.severity}</span>
                <span className="text-slate-800">{g.message}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function RowSection({ title, rows, onAdd, onRemove, render }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold">{title} <span className="text-xs font-normal text-slate-500">({rows.length})</span></h4>
        <Button size="sm" variant="outline" onClick={onAdd}><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
      </div>
      <div className="space-y-2">
        {rows.length === 0 && <div className="text-xs text-slate-500 py-2">None extracted.</div>}
        {rows.map((row, i) => (
          <div key={row._k || i} className="grid grid-cols-12 gap-2 items-center">
            {render(row, i)}
            <Button size="icon" variant="ghost" className="col-span-1" onClick={() => onRemove(i)}><Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function VisitGrantsSection({ visits, onChange }) {
  const [openIdx, setOpenIdx] = React.useState(null);
  const totalActivities = visits.reduce((s, v) => s + (v.activities?.length || 0), 0);

  const updateVisit = (i, k, v) => {
    const next = [...visits];
    next[i] = { ...next[i], [k]: v };
    onChange(next);
  };
  const removeVisit = (i) => onChange(visits.filter((_, j) => j !== i));
  const addVisit = () => onChange([...visits, { _k: Math.random().toString(36).slice(2), visit_name: "", visit_code: "", grant_amount: 0, frequency: "per patient", activities: [] }]);
  const addActivity = (vi) => {
    const next = [...visits];
    next[vi] = { ...next[vi], activities: [...(next[vi].activities || []), { _k: Math.random().toString(36).slice(2), name: "", amount: 0, description: "" }] };
    onChange(next);
  };
  const updateActivity = (vi, ai, k, v) => {
    const next = [...visits];
    const acts = [...next[vi].activities];
    acts[ai] = { ...acts[ai], [k]: v };
    next[vi] = { ...next[vi], activities: acts };
    onChange(next);
  };
  const removeActivity = (vi, ai) => {
    const next = [...visits];
    next[vi] = { ...next[vi], activities: next[vi].activities.filter((_, j) => j !== ai) };
    onChange(next);
  };
  const sumActivities = (acts) => (acts || []).reduce((s, a) => s + Number(a.amount || 0), 0);

  return (
    <div data-testid="visit-grants-section">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold flex items-center gap-2">
          <ListTree className="w-4 h-4 text-[#0A4C95]" />
          Visit Grants
          <span className="text-xs font-normal text-slate-500">({visits.length} visits · {totalActivities} activities)</span>
        </h4>
        <Button size="sm" variant="outline" onClick={addVisit}><Plus className="w-3.5 h-3.5 mr-1" /> Add Visit</Button>
      </div>
      <div className="space-y-2">
        {visits.length === 0 && <div className="text-xs text-slate-500 py-2">No visits extracted.</div>}
        {visits.map((v, vi) => {
          const isOpen = openIdx === vi;
          const sum = sumActivities(v.activities);
          const mismatch = sum > 0 && Math.abs(sum - Number(v.grant_amount || 0)) > 0.5;
          return (
            <div key={v._k || vi} className="border border-slate-200 rounded-md bg-white overflow-hidden">
              <div className="grid grid-cols-12 gap-2 items-center p-2">
                <button
                  type="button"
                  className="col-span-1 inline-flex items-center justify-center text-slate-500 hover:text-[#0A4C95]"
                  onClick={() => setOpenIdx(isOpen ? null : vi)}
                  data-testid={`toggle-visit-${vi}`}
                  title={`${(v.activities?.length || 0)} activities`}
                >
                  {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                <Input className="col-span-3" placeholder="Visit Name" value={v.visit_name} onChange={(e) => updateVisit(vi, "visit_name", e.target.value)} />
                <Input className="col-span-2" placeholder="Code" value={v.visit_code} onChange={(e) => updateVisit(vi, "visit_code", e.target.value)} />
                <Input className="col-span-2" type="number" placeholder="$" value={v.grant_amount} onChange={(e) => updateVisit(vi, "grant_amount", e.target.value)} />
                <Select value={v.frequency} onValueChange={(val) => updateVisit(vi, "frequency", val)}>
                  <SelectTrigger className="col-span-3"><SelectValue /></SelectTrigger>
                  <SelectContent>{["per patient","per cycle","once"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="icon" variant="ghost" className="col-span-1" onClick={() => removeVisit(vi)}><Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" /></Button>
              </div>
              {(v.activities?.length > 0 || isOpen) && (
                <div className={`bg-slate-50 border-t border-slate-200 px-3 py-2 ${isOpen ? "" : "hidden"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-600">
                      Activity breakdown ({v.activities?.length || 0})
                      {sum > 0 && (
                        <span className={`ml-2 font-mono-fin ${mismatch ? "text-red-600" : "text-emerald-600"}`}>
                          ∑ ${sum.toLocaleString()} {mismatch ? `≠ $${Number(v.grant_amount).toLocaleString()}` : "✓"}
                        </span>
                      )}
                    </div>
                    <Button size="sm" variant="outline" onClick={() => addActivity(vi)}><Plus className="w-3 h-3 mr-1" /> Activity</Button>
                  </div>
                  {(v.activities || []).length === 0 && <div className="text-xs text-slate-400 py-1">No itemized breakdown.</div>}
                  {(v.activities || []).map((a, ai) => (
                    <div key={a._k || ai} className="grid grid-cols-12 gap-2 items-center mb-1">
                      <Input className="col-span-5 h-8" placeholder="Activity name (e.g. Physical exam)" value={a.name} onChange={(e) => updateActivity(vi, ai, "name", e.target.value)} />
                      <Input className="col-span-2 h-8" type="number" placeholder="$" value={a.amount} onChange={(e) => updateActivity(vi, ai, "amount", e.target.value)} />
                      <Input className="col-span-4 h-8" placeholder="Notes" value={a.description || ""} onChange={(e) => updateActivity(vi, ai, "description", e.target.value)} />
                      <Button size="icon" variant="ghost" className="col-span-1 h-8 w-8" onClick={() => removeActivity(vi, ai)}><Trash2 className="w-3.5 h-3.5 text-slate-400 hover:text-red-500" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function InvoicablesSection({ rows, onAdd, onChange }) {
  const updateRow = (i, k, v) => {
    const next = [...rows];
    next[i] = { ...next[i], [k]: v };
    onChange(next);
  };
  const removeRow = (i) => onChange(rows.filter((_, j) => j !== i));

  // Group by category for summary
  const byCat = {};
  for (const r of rows) {
    const c = r.category || "other";
    byCat[c] = (byCat[c] || 0) + 1;
  }
  const totalAmount = rows.reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <div data-testid="invoicables-section">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold">
          Invoicables
          <span className="text-xs font-normal text-slate-500 ml-2">
            ({rows.length} items · ${totalAmount.toLocaleString()})
          </span>
        </h4>
        <Button size="sm" variant="outline" onClick={onAdd}><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
      </div>
      {rows.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {Object.entries(byCat).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
            <Badge key={cat} variant="outline" className={CAT_COLORS[cat] || CAT_COLORS.other}>
              {cat.replace(/_/g, " ")} · {count}
            </Badge>
          ))}
        </div>
      )}
      <div className="space-y-2">
        {rows.length === 0 && <div className="text-xs text-slate-500 py-2">None extracted.</div>}
        {rows.map((r, i) => (
          <div key={r._k || i} className="grid grid-cols-12 gap-2 items-center">
            <Input className="col-span-3" placeholder="Name" value={r.name} onChange={(e) => updateRow(i, "name", e.target.value)} />
            <Select value={r.category || "other"} onValueChange={(v) => updateRow(i, "category", v)}>
              <SelectTrigger className="col-span-2"><SelectValue /></SelectTrigger>
              <SelectContent>{INV_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
            <Input className="col-span-2" type="number" placeholder="$" value={r.amount} onChange={(e) => updateRow(i, "amount", e.target.value)} />
            <Input className="col-span-2" placeholder="When billable" value={r.when_billable || ""} onChange={(e) => updateRow(i, "when_billable", e.target.value)} title="e.g. at SIV, monthly, at LPLV" />
            <Input className="col-span-2" placeholder="Notes" value={r.description || ""} onChange={(e) => updateRow(i, "description", e.target.value)} />
            <Button size="icon" variant="ghost" className="col-span-1" onClick={() => removeRow(i)}><Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}
